import { useLayoutEffect, useMemo, useRef } from 'react';
import { typography, radius } from '@/shared/theme/tokens';
import { useFurnaceSvg } from '../hooks/useFurnaceSvg';
import { Sparkline } from './Sparkline';
import { LIVE_STATUS_META, TERM } from '../liveModeMeta';
import { Pane, PaneHeader } from './Pane';

const MONO = typography.fontFamily.mono;
const STATUS_RANK = { critical: 0, warning: 1, ok: 2 };

// One callout per parameter, worst-status first, capped to keep the schematic
// readable. Stale params are never floated as a live reading.
function pickCallouts(params, max = 3) {
  const byName = new Map();
  for (const p of params) {
    if (p.isStale) continue;
    const cur = byName.get(p.parameterName);
    if (!cur || STATUS_RANK[p.status] < STATUS_RANK[cur.status]) byName.set(p.parameterName, p);
  }
  return [...byName.values()]
    .sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status])
    .slice(0, max);
}

/**
 * Furnace schematic pane — identity + spatial anchor, not the dominant object.
 * The plant SVG renders as a monochrome wireframe on the terminal void, with
 * live value chips overlaid.
 */
export function FurnaceHero({ svgUrl, params = [] }) {
  const hostRef = useRef(null);
  const { data: markup, isLoading, isError } = useFurnaceSvg(svgUrl);
  const callouts = useMemo(() => pickCallouts(params), [params]);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host || !markup) return;
    host.innerHTML = markup;
    const svg = host.querySelector('svg');
    if (svg) {
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      svg.style.display = 'block';
      svg.style.maxHeight = '100%';
    }
  }, [markup]);

  return (
    <Pane>
      <PaneHeader
        title="Furnace"
        accent={TERM.accent}
        right={(
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: MONO, fontSize: typography.fontSize.xs, letterSpacing: typography.letterSpacing.wide, color: TERM.ok }}>
            <span style={{ width: 6, height: 6, borderRadius: radius.full, background: TERM.ok }} />
            SCHEMATIC
          </span>
        )}
      />

      <div style={{ position: 'relative', flex: 1, minHeight: 0, background: TERM.void, overflow: 'hidden' }}>
        {/* SVG wireframe backdrop */}
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '14px 14px', opacity: 0.82,
          // Render the plant SVG as a cool steel-cyan wireframe ghost on the void.
          filter: 'brightness(0) invert(0.64) sepia(0.5) saturate(2.4) hue-rotate(150deg) brightness(1.05)',
        }}>
          {isLoading && <span style={{ fontFamily: MONO, fontSize: typography.fontSize.sm, color: TERM.inkFaint }}>LOADING SCHEMATIC…</span>}
          {isError && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 32, color: TERM.inkFaint }}>broken_image</span>
              <span style={{ fontFamily: MONO, fontSize: typography.fontSize.sm, color: TERM.inkFaint }}>SCHEMATIC UNAVAILABLE</span>
            </div>
          )}
          {!isLoading && !isError && (
            <div ref={hostRef} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
          )}
        </div>

        {/* Healthy state chip */}
        {!isLoading && !isError && callouts.length === 0 && (
          <div style={{
            position: 'absolute', top: 12, right: 12,
            display: 'flex', alignItems: 'center', gap: 7, padding: '6px 10px',
            background: TERM.panel, border: `1px solid ${LIVE_STATUS_META.ok.border}`,
          }}>
            <span style={{ width: 8, height: 8, background: TERM.ok }} />
            <span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.wide, color: TERM.ink }}>ALL SYSTEMS NOMINAL</span>
          </div>
        )}

        {/* Live value callouts */}
        <div style={{
          position: 'absolute', top: 12, right: 12, bottom: 12, zIndex: 2,
          display: 'flex', flexDirection: 'column', gap: 8, width: 170, pointerEvents: 'none',
        }}>
          {callouts.map((p) => {
            const meta = LIVE_STATUS_META[p.status] ?? LIVE_STATUS_META.ok;
            return (
              <div key={p.id} style={{
                background: TERM.panel, border: `1px solid ${meta.border}`, padding: '7px 9px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 7, height: 7, background: meta.color, flexShrink: 0 }} />
                  <span style={{
                    fontFamily: MONO, fontSize: typography.fontSize.xs, color: TERM.inkDim,
                    letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {p.parameterName}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 3 }}>
                  <span style={{ fontFamily: MONO, fontSize: typography.fontSize['4xl'], fontWeight: typography.fontWeight.bold, color: meta.color, fontVariantNumeric: 'tabular-nums' }}>
                    {p.liveValue}
                  </span>
                  {p.unit && <span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: TERM.inkFaint }}>{p.unit}</span>}
                </div>
                <div style={{ marginTop: 3 }}>
                  <Sparkline data={p.buffer} color={meta.color} height={18} animate={false} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Pane>
  );
}
